<script lang="ts" setup> 
    import {NoTzDate} from "../lib/DateUtils";
    import {onMounted,computed,ref,watch,nextTick,reactive} from 'vue';
    import DrugInformation from "../components/DrugInformation.vue";
    import RxInteractions from "../components/RxInteractions.vue";
    import {confirmDialog,popAlert} from '../lib/Notification';
    import {UpdatePrescriptionQueue,IQueueAudits,prescriptionStatusCodes,RxQueueStatus,minimum_signing_credential_type,ApprovalType} from '../lib/Models/PatientRxModel';  
    import {interactionType,severityLevel,getSeverityDescription} from '../lib/Models/InteractionModel';
    import {DisplayStyle, IconTypes} from "../Enums/UiEnums";
    import { iconClass } from '../lib/StyleHelpers';
    import Icon from "../components/Icon.vue";   
    import DropDownSplitButton from '../components/DropDownSplitButton.vue';
    import DropDownButtonItem from "../components/DropDownButtonItem.vue";  

    enum InformationType {
        All = 0,
        Drug = 1,
        Allergy = 2,
        Disease = 3
    };
    enum RxSelectedClass {
        Selected = "prescription-selected",
        Unselected = "prescription-unselected"
    };
    enum RxBorderColor {
        Pending = "prescription-pending",
        Ready = "prescription-ready",
        Denied = "prescription-denied"
    };
    enum RxBackGround {
        Active = "prescription-active",
        Inactive = "prescription-inactive"
    };

    const rxReadyField = ref(); 
    const isStatusChecked = ref(false); 
    const formData = reactive <
    {   
        showDrug : {Information: boolean, DrugInteraction: boolean, AllergyInteraction: boolean, DiseaseInteraction: boolean},
        blockClass: string,
        status: {newQueue: RxQueueStatus, currentQueue: RxQueueStatus},
        prescription: {icon: IconTypes, style: DisplayStyle, class: string, title: string},
        level: {icon: IconTypes,  class: string, title: string}
    }>
    ({     
        showDrug : {Information: false, DrugInteraction: false, AllergyInteraction: false, DiseaseInteraction: false},
        blockClass: "",
        status: {newQueue: RxQueueStatus.Pending, currentQueue: RxQueueStatus.Pending},
        prescription: {icon: IconTypes.Unknown, style: DisplayStyle.Primary, class: "text-isalus-primary fs-5", title: "Prescription is Pending"},
        level: {icon: IconTypes.RxBase,  class: "prescription-level-base fs-6", title: "Click here to display Drug Information"}
    });

    const props = withDefaults(defineProps<{
            index: number,
            completed: ApprovalType,
            patientID: number,
            prescriberID: number,
            prescriptionID: number,
            scheduleLevel?: number,
            writtenDateUTC?: NoTzDate,
            effectiveDate?: NoTzDate,
            voidAfterDate?: NoTzDate,
            drugName: string,
            fullSig?: string,
            fullDispense?: string,
            dispenseAsWritten?: string,
            refillDescription?: string,
            queueStatus? : RxQueueStatus,
            queueStatusCreation?: string,
            pharmacyInformation?: string,
            pharmacyPhone?: string
            pharmacyFax?: string,
            saveEnabled: number,
            credentialTypeID: number,
            providerFullName?: string,
            providerAccess: number,
            allergySeverityLevel? : severityLevel,
            drugSeverityLevel? : severityLevel,
            diseaseSeverityLevel? : severityLevel,
            rxStatusCode: number,
            currentId: number
        }>(), { 
            scheduleLevel: 0,
            queueStatus : RxQueueStatus.Pending,
            allergySeverityLevel: severityLevel.NoneFound,
            drugSeverityLevel: severityLevel.NoneFound,
            diseaseSeverityLevel: severityLevel.NoneFound,
            credentialTypeID: 0
    }); 

    watch(() => props.currentId, (newID,oldID) => { 
        if ((newID !== 0) && (newID !== oldID) && (props.providerAccess === 1) && (newID === props.prescriptionID)) {  
            setFieldFocus(); 
            setBlockClassSelected();
        } 
        else {
            setBlockClassActive();
        }
    });
    watch(() => props.queueStatus, (newID,oldID) => { 
        isStatusChecked.value = (newID === RxQueueStatus.ReadyToSign ? true : false); 
    });

    const emit=defineEmits(["change-rx-status","reset_current_id"]);  
    const queueName = computed<string>(() => "queueStatus" + props.prescriptionID);
    const queueIdStatus = computed<string>(() => "queueIdStatus" + props.prescriptionID);  
    const queueDisabled = computed(()=>  {
        if (props.saveEnabled === 0)
            return true;
        if (props.providerAccess === 0)    
            return true;
        if ((props.scheduleLevel > 0) && (props.credentialTypeID < minimum_signing_credential_type))
            return true;
        return false;
    }); 
    const pharmacyName = computed(()=>  {
        if (!props.pharmacyInformation)
            return;
        return props.pharmacyInformation.split(";")[0];
    });
    const pharmacyAddress = computed(()=>  {
        if (!props.pharmacyInformation)
            return;
        return props.pharmacyInformation.split(";")[1] ?? "";
    }); 
    const minSevertyLevel = computed(()=>  {
        let severity_level : number = severityLevel.CalculationOnly; // Maximize this to get lowest level...
        if ((props.drugSeverityLevel) && (props.drugSeverityLevel > severityLevel.None) && (props.drugSeverityLevel < severity_level)) {
            severity_level = props.drugSeverityLevel; 
        }
        if ((props.allergySeverityLevel) && (props.allergySeverityLevel > severityLevel.None) && (props.allergySeverityLevel < severity_level)) {
            severity_level = props.allergySeverityLevel; 
        }
        if ((props.diseaseSeverityLevel) && (props.diseaseSeverityLevel > severityLevel.None) && (props.diseaseSeverityLevel < severity_level)) {
            severity_level = props.diseaseSeverityLevel; 
        }
        return (severity_level === severityLevel.CalculationOnly ? severityLevel.None : severity_level);
    });

    onMounted(() => { 
        isStatusChecked.value = (props.queueStatus === RxQueueStatus.ReadyToSign ? true : false);
        formData.status.newQueue = props.queueStatus;
        formData.status.currentQueue = formData.status.newQueue; 
        
        setPrescriptionIconInformation(props.queueStatus); 
        setBlockClassActive();  
        switch (minSevertyLevel.value) {
            case severityLevel.Contraindicated:
                formData.level = {icon: IconTypes.RxLevel1, class: "btn btn-sm btn-outline-danger prescription-level-1", title: `Click here to display ${getSeverityDescription(severityLevel.Contraindicated)} Interactions`}
                break;
            case severityLevel.Severe:
                formData.level = {icon: IconTypes.RxLevel2, class: "btn btn-sm btn-outline-warning prescription-level-2", title: `Click here to display ${getSeverityDescription(severityLevel.Severe)} Interactions`}
                break;
            case severityLevel.Moderate:
                formData.level = {icon: IconTypes.RxLevel3, class: "btn btn-sm btn-outline-warning prescription-level-3", title: `Click here to display ${getSeverityDescription(severityLevel.Moderate)} Interactions`}
                break;
            case severityLevel.Undetermined:
                formData.level = {icon: IconTypes.RxLevel9, class: "btn btn-sm btn-outline-primary prescription-level-base", title: `Click here to display ${getSeverityDescription(severityLevel.Undetermined)} Interactions`}
                break;
            case severityLevel.Unknown:
                formData.level = {icon: IconTypes.RxLevel10, class: "btn btn-sm btn-outline-primary prescription-level-base", title: "Click here to display Drug Information"}
                break;
           default:
                formData.level = {icon: IconTypes.RxBase, class: "btn btn-sm btn-outline-primary prescription-level-base", title: "Click here to display Drug Information"}
                break;
        }
    });

    function setPrescriptionIconInformation(newStatus : RxQueueStatus) : void {
        
        if (props.completed === ApprovalType.Completed) {
            formData.prescription = {icon: IconTypes.Complete, style: DisplayStyle.Success, class: "text-isalus-primary fs-5", title: "Prescription is Completed"};
        }
        else {
            switch (newStatus) {
                case RxQueueStatus.ReadyToSign:
                    formData.prescription = {icon: IconTypes.Ready, style: DisplayStyle.Success, class: "text-success fs-5", title: "Ready to Sign"};
                    break;
                case RxQueueStatus.Denied: 
                    formData.prescription = {icon: IconTypes.Trashcan, style: DisplayStyle.Danger, class: "text-danger fs-5", title: "Prescription Denied"};
                    break;
                default:
                    formData.prescription = {icon: IconTypes.Unknown, style: DisplayStyle.Primary, class: "text-isalus-primary fs-5", title: "Prescription is Pending"};
            }
        }
    }

    function setBlockClassDenied() : void {
        setBlockClass(RxSelectedClass.Selected);
    }

    function setBlockClassActive() : void {
        setBlockClass(RxSelectedClass.Unselected);
    }

    function setBlockClassSelected() : void {
        setBlockClass(RxSelectedClass.Selected); 
    }

    function setBlockClass(selected_status : RxSelectedClass) : void {  
        let border_color = "";

        switch (formData.status.newQueue) {
            case RxQueueStatus.ReadyToSign:
                border_color = RxBorderColor.Ready;
                break;
            case RxQueueStatus.Pending:
                border_color = RxBorderColor.Pending;
                break;
            case RxQueueStatus.Denied:
                border_color = RxBorderColor.Denied;
                break;
        }
        formData.blockClass =
            "prescription-base rounded-3 " +
            border_color + " " + 
            selected_status + " " +
            (queueDisabled.value ? RxBackGround.Inactive : RxBackGround.Active);
    }

    async function setFieldFocus()  : Promise<void>  {
        await nextTick();
        rxReadyField.value.focus(); 
    }

    function queueClick1() : void {
        const element = <HTMLInputElement> document.getElementById(queueIdStatus.value);
        if (element.checked) { 
            queueClick(RxQueueStatus.ReadyToSign);
        }
        else {
            queueClick(RxQueueStatus.Pending);
        }
    }

    function queueClick(queueValue : RxQueueStatus) : void { 
        emit('reset_current_id',props.prescriptionID); 
        switch (queueValue) {
            case RxQueueStatus.Denied:  
                setPrescriptionIconInformation(RxQueueStatus.Denied);
                StatusChange(RxQueueStatus.Denied);
                break;
            case RxQueueStatus.ReadyToSign:  
                setPrescriptionIconInformation(RxQueueStatus.ReadyToSign);
                StatusChange(RxQueueStatus.ReadyToSign);
                break;
            default:  
                setPrescriptionIconInformation(RxQueueStatus.Pending);
                StatusChange(RxQueueStatus.Pending);
        }
    }  

    async function StatusChange(newStatus : RxQueueStatus) : Promise<void> { 
        if (newStatus === RxQueueStatus.Denied) { 
            CheckDenial();
            return;
        }
        const queueRecords : IQueueAudits = {
            QueueRecords: [{PrescriptionID: props.prescriptionID, QueueStatusID: newStatus, PrescriptionRxStatusCode: props.rxStatusCode}],
            AuditRecords: [{StatementID: props.prescriptionID.toString(), Description: "Queue Status", OldValue: formData.status.currentQueue?.toString(), NewValue: newStatus.toString()}]};
        const respSave = await UpdatePrescriptionQueue(queueRecords);  
 
        if (respSave.error) {  
            popAlert(respSave.error.error ?? "Unknown Error","Prescription Queue Update");
            resetStatus();
            return;
        }     
        formData.status.newQueue = newStatus;
        formData.status.currentQueue = formData.status.newQueue;
        setBlockClassSelected();
        emit('change-rx-status',formData.status.newQueue, props.index, props.prescriptionID); 
    } 

    async function CheckDenial()  : Promise<void>  {
        formData.status.newQueue = RxQueueStatus.Denied;
        setBlockClassDenied(); 
        const result = await confirmDialog("Are you sure you want to deny this prescription?<BR/><BR/>This will result in the prescription status being set to error, and it's removal from this queue.", { titleHTML: `Prescription Denial for:<br/>${props.drugName}`,RejectText: "Cancel", ConfirmText: "Confirm" });  
        if (!result) {
            resetStatus();
            return;
        } 
        const queueRecords : IQueueAudits = {
            QueueRecords: [{PrescriptionID: props.prescriptionID, QueueStatusID: RxQueueStatus.Denied, PrescriptionRxStatusCode: prescriptionStatusCodes.Error}],
            AuditRecords: 
                [
                    {StatementID: props.prescriptionID.toString(), Description: "Queue Status", OldValue: formData.status.currentQueue?.toString(), NewValue: RxQueueStatus.Denied.toString()},
                    {StatementID: props.prescriptionID.toString(), Description: "Status Code", OldValue: props.rxStatusCode?.toString(), NewValue: prescriptionStatusCodes.Error.toString()},
                ]};
        const respSave = await UpdatePrescriptionQueue(queueRecords);  

        if (respSave.error) {  
            popAlert(respSave.error.error ?? "Unknown Error","Prescription Queue Update");
            formData.status.newQueue = formData.status.currentQueue;
            return;
        }
        formData.status.newQueue = RxQueueStatus.Denied;
        formData.status.currentQueue = formData.status.newQueue;
        setBlockClassSelected();
        emit('change-rx-status',formData.status.newQueue, props.index, props.prescriptionID);
    }

    function resetStatus() : void {            
        formData.status.newQueue = formData.status.currentQueue;
        switch (formData.status.currentQueue) {
            case RxQueueStatus.Denied: 
                setPrescriptionIconInformation(RxQueueStatus.Denied);
                break;
            case RxQueueStatus.ReadyToSign: 
                setPrescriptionIconInformation(RxQueueStatus.ReadyToSign);
                break;
            case RxQueueStatus.Pending: 
                setPrescriptionIconInformation(RxQueueStatus.Pending);
                break;
        } 
        setFieldFocus(); 
    }

    function displayInformation(inputValue : InformationType) : void {
        closeInformation();
        switch (inputValue) {
            case InformationType.Drug:
                formData.showDrug.DrugInteraction = true;
                break;
            case InformationType.Allergy:
                formData.showDrug.AllergyInteraction = true;
                break;
            case InformationType.Disease:
                formData.showDrug.DiseaseInteraction = true;
                break;
            default: 
                formData.showDrug.Information = true;
                break;
        }
    }

    function closeInformation() : void { 
        formData.showDrug.Information = false;
        formData.showDrug.DrugInteraction = false;
        formData.showDrug.AllergyInteraction = false;
        formData.showDrug.DiseaseInteraction = false;
    }

    function onClickedDropDownButton() {
        setBlockClassSelected();
        displayInformation(InformationType.All);
    }
    
    function onClickedDropDownItem(id: any) {
        setBlockClassSelected();
        switch (id) {
            case "Drug":
                displayInformation(InformationType.Drug);
                break;
            case "Allergy":
                displayInformation(InformationType.Allergy);
                break;
            default:
                displayInformation(InformationType.Disease);
        }
    }
</script>
<style scoped>
    .prescription-base {
        color: black;
        padding: 5px;
    }
    .prescription-pending { 
        border: 1pt solid rgba(var(--bs-primary-rgb), var(--bs-text-opacity));
    }
    .prescription-ready { 
        border: 1pt solid rgba(var(--bs-success-rgb), var(--bs-text-opacity));
    }
    .prescription-denied { 
        border: 1pt solid rgba(var(--bs-danger-rgb), var(--bs-text-opacity));
    }
    .prescription-selected {
        border-color: #629deb;
        box-shadow: 0 0 5px #629deb;
    }
    .prescription-unselected {
        border-style: solid;
        border-width: 1px;
    }
    .prescription-label {
        font-size: .6rem;
    }
    .prescription-active { 
        background-color: white; 
    }
    .prescription-inactive {
        background-color: #f1f1f1;
    }
    .prescription-level-base {
        color: #6692CD;
    }
    .prescription-level-1 {
        color:  #b42942;
    }
    .prescription-level-2 {
        color: #fde070;
    }
    .prescription-level-3 {
        color: #fde070;
    }
    .prescription-level-9 {
        color:  #00385c; 
    }
    .prescription-level-10 {
        color: #00385c; 
    }
    .prescription_trash {
        font-size: .85rem;
    }
</style>

<template>
   <div v-show="formData.showDrug.Information"  class="row">
        <div class="col-auto label-isalus-small text-primary">
            <DrugInformation 
                :showDialog="formData.showDrug.Information"
                :drugName="props.drugName"    
                :patientID="props.patientID"    
                :prescriptionID="props.prescriptionID"
                :minSevertyLevel="minSevertyLevel"
                :allergySeverity="props.allergySeverityLevel"
                :drugySeverity="props.drugSeverityLevel"
                :diseaseSeverity="props.diseaseSeverityLevel"
                @closeInformation="closeInformation" 
                >
            </DrugInformation>
        </div>
    </div>
    <div v-show="formData.showDrug.DrugInteraction"  class="row">
        <div class="col-auto label-isalus-small text-primary">
            <RxInteractions 
                :showDialog="formData.showDrug.DrugInteraction"
                :patientID="props.patientID"    
                :prescriptionID="props.prescriptionID"
                :interactionType="interactionType.Drug"
                @closeInformation="closeInformation">
            </RxInteractions>
        </div>
    </div>
    <div v-show="formData.showDrug.AllergyInteraction"  class="row">
        <div class="col-auto label-isalus-small text-primary">
            <RxInteractions 
                :showDialog="formData.showDrug.AllergyInteraction"
                :patientID="props.patientID"    
                :prescriptionID="props.prescriptionID"
                :interactionType="interactionType.Allergy"
                @closeInformation="closeInformation">
            </RxInteractions>
        </div>
    </div>
    <div v-show="formData.showDrug.DiseaseInteraction"  class="row">
        <div class="col-auto label-isalus-small text-primary">
            <RxInteractions 
                :showDialog="formData.showDrug.DiseaseInteraction"
                :patientID="props.patientID"    
                :prescriptionID="props.prescriptionID"
                :interactionType="interactionType.Disease"
                @closeInformation="closeInformation">
            </RxInteractions>
        </div>
    </div>
    <div :class="formData.blockClass"> 
        <table style="table-layout: fixed; width: 100%;">
            <colgroup><col style="width: 30%"><col style="width: 15%"><col style="width: 55%"><col style="width: 35px">"</colgroup>
            <tr>
                <td colspan="3">
                    <DropDownSplitButton 
                        :showDownArrow="minSevertyLevel > 0" 
                        :buttonTitle="'Click here to display prescribing information'" 
                        :dropDownButtonTitle="'Show Interactions'"
                        :baseClass="formData.level.class" 
                        @onButtonClick="onClickedDropDownButton">
                        <template v-slot:button>
                            <i :class="iconClass(formData.level.icon)" aria-hidden="true"></i>
                        </template>    
                        <DropDownButtonItem  v-show="props.drugSeverityLevel > 0" data="Drug" @click="onClickedDropDownItem" :baseClass="'label-isalus-small text-isalus-primary px-sm-2'">Drug Interactions</DropDownButtonItem>
                        <DropDownButtonItem  v-show="props.allergySeverityLevel > 0" data="Allergy" @click="onClickedDropDownItem" :baseClass="'label-isalus-small text-isalus-primary px-sm-2'">Allergy Interactions</DropDownButtonItem>
                        <DropDownButtonItem  v-show="props.diseaseSeverityLevel > 0" data="Disease" @click="onClickedDropDownItem" :baseClass="'label-isalus-small text-isalus-primary px-sm-2'">Disease Interactions</DropDownButtonItem>
                    </DropDownSplitButton>  
                    <span class="fw-bold fs-6 ps-sm-2 text-isalus-primary">{{props.drugName}}</span>
                </td>
                <td>  
                    <Icon 
                        :icon="formData.prescription.icon" 
                        :displayStyle="formData.prescription.style" 
                        :class="formData.prescription.class" 
                        :title="formData.prescription.title" /> 
                </td>
            </tr> 
            <tr v-show="formData.status.newQueue !== RxQueueStatus.ReadyToSign" >
                <td colspan="4">
                    <span class="fw-bold label-isalus-small">{{props.fullSig}}</span>
                </td>
            </tr>
            <tr v-show="formData.status.newQueue !== RxQueueStatus.ReadyToSign" >
                <td style="vertical-align: top;">
                    <table>
                        <tr>
                            <td class="text-muted prescription-label">Dispense</td>
                        </tr>
                        <tr>
                            <td class="label-isalus-small">{{props.fullDispense}}</td>
                        </tr>
                        <tr>
                            <td class="label-isalus-small">Refills: {{props.refillDescription}}</td>
                        </tr>
                        <tr>
                            <td class="label-isalus-small">DAW: {{props.dispenseAsWritten}}</td>
                        </tr>
                    </table>
                </td>
                <td style="vertical-align: top;">
                    <table>
                        <tr>
                            <td class="text-muted prescription-label">Dates</td>
                        </tr>
                        <tr>
                            <td>Written: {{props.writtenDateUTC}}</td>
                        </tr>
                        <tr>
                            <td>Effective: {{props.effectiveDate}}</td>
                        </tr>
                        <tr>
                            <td>Void: {{props.voidAfterDate}}</td>
                        </tr>
                    </table>
                </td>
                <td style="vertical-align: top;" colspan="2">
                    <table>
                        <tr>
                            <td class="text-muted prescription-label">Pharmacy</td>
                        </tr>
                        <tr>
                            <td>{{pharmacyName}}</td>
                        </tr>
                        <tr>
                            <td>{{pharmacyAddress}}</td>
                        </tr>
                        <tr>
                            <td>Phone: {{props.pharmacyPhone}} Fax: {{props.pharmacyFax}}</td>
                        </tr>
                    </table>
                </td>
            </tr>
            <tr height="30" style="vertical-align: middle;">     
                <td colspan=2 class="text-muted label-isalus-small fst-italic">Rx #{{props.prescriptionID}} ({{providerFullName}}) Queued By: {{props.queueStatusCreation}}</td>
                <td class="label-isalus-small text-end" colspan="2">  
                    <span v-show="props.completed === ApprovalType.Pending" class="prescription_trash px-2 pb-1">
                        <Icon 
                            :icon="IconTypes.Trashcan" 
                            :displayStyle="DisplayStyle.Secondary" 
                            class="text-isalus-primary" 
                            title="Click here to deny prescription request" 
                            @click="queueClick(RxQueueStatus.Denied)"/>
                    </span> 
                    <input v-show="props.completed === ApprovalType.Pending" ref="rxReadyField" v-model="isStatusChecked" :disabled="true === queueDisabled" type="checkbox" :name="queueName" :id="queueIdStatus" @focus="setBlockClassSelected" @focusout="setBlockClassActive" @change="queueClick1">
                    <label v-show="props.completed === ApprovalType.Pending" class="fw-bold label-isalus-small px-2" :for="queueIdStatus">Ready to {{props.scheduleLevel > 0 ? 'Sign' : 'Send'}}</label> 
                </td>
            </tr>
        </table>
    </div> 
</template>